/**
 * IndexedDB cache for large map payloads (map/all-data, admin boundaries).
 * Cookies/localStorage are not suitable — those quotas are tiny vs GeoJSON megabytes.
 */

const DB_NAME = 'elhalawany-map-cache'
const STORE_NAME = 'entries'
const DB_VERSION = 1

/** Default durable TTL: 24 hours */
export const MAP_DATA_PERSISTENT_TTL_MS = 24 * 60 * 60 * 1000

let dbPromise = null

function canUseIndexedDb() {
  return typeof indexedDB !== 'undefined'
}

function openDb() {
  if (!canUseIndexedDb()) {
    return Promise.reject(new Error('IndexedDB unavailable'))
  }
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => {
      dbPromise = null
      reject(request.error || new Error('Failed to open map cache DB'))
    }
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
  })

  return dbPromise
}

function runStore(mode, executor) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const store = tx.objectStore(STORE_NAME)
    let request
    try {
      request = executor(store)
    } catch (error) {
      reject(error)
      return
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  }))
}

/**
 * @param {string} key
 * @param {number} [ttlMs]
 * @returns {Promise<{ data: any, fetchedAt: number }|null>}
 */
export async function getPersistentMapCache(key, ttlMs = MAP_DATA_PERSISTENT_TTL_MS) {
  if (!key || !canUseIndexedDb()) return null
  try {
    const entry = await runStore('readonly', store => store.get(key))
    if (!entry || entry.data == null) return null
    const fetchedAt = Number(entry.fetchedAt) || 0
    if (!fetchedAt || Date.now() - fetchedAt > ttlMs) {
      // Expired — best-effort cleanup
      removePersistentMapCache(key).catch(() => {})
      return null
    }
    return { data: entry.data, fetchedAt }
  } catch (error) {
    return null
  }
}

/**
 * @param {string} key
 * @param {any} data
 */
export async function setPersistentMapCache(key, data) {
  if (!key || data == null || !canUseIndexedDb()) return false
  try {
    await runStore('readwrite', store => store.put({
      key,
      data,
      fetchedAt: Date.now(),
    }))
    return true
  } catch (error) {
    // Quota / private mode — ignore; memory cache still works for the session.
    return false
  }
}

export async function removePersistentMapCache(key) {
  if (!key || !canUseIndexedDb()) return
  try {
    await runStore('readwrite', store => store.delete(key))
  } catch (error) {
    // ignore
  }
}

function iterateStore(mode, range, visit) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const request = tx.objectStore(STORE_NAME).openCursor(range)
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        resolve()
        return
      }
      visit(cursor)
      cursor.continue()
    }
    request.onerror = () => reject(request.error)
  }))
}

/** Delete every entry whose key starts with `prefix`. */
export async function removePersistentMapCacheByPrefix(prefix) {
  if (!prefix || !canUseIndexedDb()) return
  try {
    await iterateStore('readwrite', IDBKeyRange.bound(prefix, `${prefix}\uffff`), cursor => cursor.delete())
  } catch (error) {
    // ignore
  }
}

/** Drop expired entries (keeps the DB from growing with stale tiles). */
export async function prunePersistentMapCache(ttlMs = MAP_DATA_PERSISTENT_TTL_MS) {
  if (!canUseIndexedDb()) return
  const now = Date.now()
  try {
    await iterateStore('readwrite', null, cursor => {
      const fetchedAt = Number(cursor.value?.fetchedAt) || 0
      if (!fetchedAt || now - fetchedAt > ttlMs) cursor.delete()
    })
  } catch (error) {
    // ignore
  }
}

export async function clearPersistentMapCache() {
  if (!canUseIndexedDb()) return
  try {
    await runStore('readwrite', store => store.clear())
  } catch (error) {
    // ignore
  }
}
